/**
 * API Client — общение Mini App с Bot Worker
 *
 * Все запросы идут через initData для авторизации.
 * Base URL настраивается через env (VITE_API_URL).
 */

import { ok, err, type Result } from '@tg-miniapp/shared-types';

// ─── Config ────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL as string || 'http://localhost:8787';

// ─── Core Request ──────────────────────────────────────────

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  initData?: string;
}

async function apiRequest<T>(
  path: string,
  options: ApiOptions = {},
): Promise<Result<T, Error>> {
  const { method = 'GET', body, initData } = options;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (initData) {
      headers['Authorization'] = `tma ${initData}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorText = await response.text();
      return err(new Error(`API error ${response.status}: ${errorText}`));
    }

    const data = await response.json() as T;
    return ok(data);
  } catch (error: unknown) {
    return err(new Error(`Network error: ${error instanceof Error ? error.message : String(error)}`));
  }
}

interface UserProfile {
  userId: string;
  role: 'owner' | 'manager' | 'assistant' | 'master' | 'admin';
  businessId: string | null;
  isAdmin: boolean;
}

interface BotInfo {
  ok: boolean;
  bot: { id: number; first_name: string; username: string };
  profile: UserProfile;
}

export async function getBotInfo(initData: string): Promise<Result<BotInfo, Error>> {
  return apiRequest<BotInfo>('/api/me', { initData });
}

// ─── Health ────────────────────────────────────────────────

interface HealthResponse {
  status: string;
  version: string;
  timestamp: number;
}

export async function getHealth(): Promise<Result<HealthResponse, Error>> {
  return apiRequest<HealthResponse>('/health');
}

// ─── Bookings ──────────────────────────────────────────────

interface BookingsResponse {
  bookings: Array<{
    id: string;
    clientTgId: string;
    slotStart: number;
    slotEnd: number;
    status: string;
    serviceName?: string;
  }>;
}

export async function getBookings(
  initData: string,
  date: string,
): Promise<Result<BookingsResponse, Error>> {
  return apiRequest<BookingsResponse>(`/api/bookings?date=${date}`, { initData });
}

// ─── Config ────────────────────────────────────────────────

import type { BotConfig } from '@tg-miniapp/shared-types';

export async function getBotConfig(
  initData: string,
): Promise<Result<BotConfig, Error>> {
  return apiRequest<BotConfig>('/api/config', { initData });
}

export async function updateBotConfig(
  initData: string,
  config: BotConfig,
): Promise<Result<void, Error>> {
  return apiRequest<void>('/api/config', {
    method: 'PUT',
    body: config,
    initData,
  });
}

export async function getPlatformSettings(initData: string) {
  return apiRequest<any>('/api/platform/settings', { initData });
}

export async function updatePlatformSettings(initData: string, settings: any) {
  return apiRequest<void>('/api/platform/settings', {
    method: 'PUT',
    body: settings,
    initData,
  });
}

// ─── Staff ──────────────────────────────────────────────────

export interface StaffMember {
  id: string;
  name: string;
  role: 'owner' | 'manager' | 'assistant' | 'master';
  permissions: any;
  isActive: boolean;
  tgUserId?: string;
}

export async function getStaffList(initData: string) {
  return apiRequest<{ staff: StaffMember[] }>('/api/staff', { initData });
}

export async function addStaffMember(initData: string, name: string, role: string, tgUserId?: string) {
  return apiRequest<{ member: StaffMember }>('/api/staff', {
    method: 'POST',
    body: { name, role, tgUserId },
    initData
  });
}

export async function updateStaffMember(initData: string, memberId: string, updates: Partial<StaffMember>) {
  return apiRequest<{ member: StaffMember }>(`/api/staff/${memberId}`, {
    method: 'PUT',
    body: updates,
    initData
  });
}

export async function deleteStaffMember(initData: string, memberId: string) {
  return apiRequest<{ ok: boolean }>(`/api/staff/${memberId}`, {
    method: 'DELETE',
    initData
  });
}

// ─── Billing ────────────────────────────────────────────────

export async function getBillingStatus(initData: string) {
  return apiRequest<any>('/api/billing/status', { initData });
}

export async function payBilling(initData: string, plan: string) {
  return apiRequest<{ ok: boolean; status: { plan: string; expiresAt: number | null } }>('/api/billing/pay', {
    method: 'POST',
    body: { plan },
    initData
  });
}

// ─── AI Agents (Platform) ────────────────────────────────────

import type { AIAgent } from '@tg-miniapp/shared-types';

export async function getAIAgents(initData: string) {
  return apiRequest<{ agents: AIAgent[] }>('/api/platform/ai-agents', { initData });
}

export async function createAIAgent(initData: string, agent: Omit<AIAgent, 'id'>) {
  return apiRequest<{ agent: AIAgent }>('/api/platform/ai-agents', {
    method: 'POST',
    body: agent,
    initData
  });
}

export async function updateAIAgent(initData: string, id: string, updates: Partial<AIAgent>) {
  return apiRequest<{ agent: AIAgent }>(`/api/platform/ai-agents/${id}`, {
    method: 'PUT',
    body: updates,
    initData
  });
}

export async function deleteAIAgent(initData: string, id: string) {
  return apiRequest<{ ok: boolean }>(`/api/platform/ai-agents/${id}`, {
    method: 'DELETE',
    initData
  });
}
